import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NotebookPen, Plus, Trash2, Pencil, MapPin, Clock, Luggage, X, ImagePlus, MessageSquare, MessageCircle, Facebook, Link2, BookOpen, Gift, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { auth } from "../../lib/firebase";

const ACTIVITY_TYPES = [
  ["hike", "🥾 Hiking / Trekking"], ["picnic", "🧺 Picnic"], ["swimming", "🏊 Swimming"], ["beach", "🏖️ Beach"],
  ["museum", "🏛️ Museum"], ["food", "🍽️ Food & Dining"], ["shopping", "🛍️ Shopping"],
  ["party", "🎈 Party"], ["birthday", "🎂 Birthday Party"], ["wedding", "💍 Wedding"], ["event", "🎉 Event"],
  ["sightseeing", "📸 Sightseeing"], ["wildlife", "🦜 Wildlife"], ["adventure", "🧗 Adventure"],
  ["relaxation", "🧘 Relaxation"], ["family", "👨‍👩‍👧 Family"], ["photography", "📷 Photography"], ["other", "➕ Other"],
];
const TYPE_LABEL = Object.fromEntries(ACTIVITY_TYPES);

const EMPTY = { title: "", notes: "", activity_type: "other", activity_other: "", date: "", time: "", location: "", trip_id: "", part_of_trip: true, links: [] };

const MAX_UPLOAD = 20 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/* Auto-shrink large or unsupported images to JPEG before upload (max 20 MB). */
async function compressImage(file) {
  if (ALLOWED_TYPES.includes(file.type) && file.size < 2.5 * 1024 * 1024) return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, 2200 / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (blob && (blob.size < file.size || !ALLOWED_TYPES.includes(file.type))) {
      return new File([blob], `${(file.name || "photo").replace(/\.\w+$/, "")}.jpg`, { type: "image/jpeg" });
    }
  } catch { /* fall back to the original file */ }
  return file;
}

/* My Travel Diaries — My Memoirs: custom daily activities (hike, picnic, museum…)
   by date & time, linked to a trip or kept outside it. */
export default function MemoirsSection() {
  const [items, setItems] = useState(null);
  const [trips, setTrips] = useState([]);
  const [imgTok, setImgTok] = useState(null); // private memoir photos need ?auth=
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // memoir id being edited
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/memoirs").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { auth.currentUser?.getIdToken().then(setImgTok).catch(() => {}); }, []);
  useEffect(() => {
    // All trips — current & upcoming first (soonest first), then past (most recent first)
    api.get("/trips").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const all = r.data.items || [];
      const cur = all.filter((t) => (t.end_date || t.start_date || "") >= today)
        .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      const past = all.filter((t) => (t.end_date || t.start_date || "") < today)
        .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
      setTrips([...cur, ...past]);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [newLink, setNewLink] = useState({ title: "", url: "" });
  const openNew = () => { setF({ ...EMPTY, date: new Date().toISOString().slice(0, 10) }); setPhotoFiles([]); setNewLink({ title: "", url: "" }); setEditing(null); setShowForm(true); };
  const openEdit = (m) => {
    setF({ title: m.title, notes: m.notes || "", activity_type: m.activity_type, activity_other: m.activity_other || "",
      date: m.date, time: m.time || "", location: m.location || "", trip_id: m.trip_id || "", part_of_trip: m.part_of_trip !== false,
      links: m.links || [] });
    setPhotoFiles([]); setNewLink({ title: "", url: "" });
    setEditing(m.id); setShowForm(true);
  };

  const addLink = (base) => {
    const url = newLink.url.trim();
    if (!url) return base || f;
    const full = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const norm = (u) => u.toLowerCase().replace(/\/+$/, "");
    if (((base || f).links || []).some((l) => norm(l.url) === norm(full))) {
      toast.error("This link is already added — duplicate rejected");
      setNewLink({ title: "", url: "" });
      return base || f;
    }
    const next = { ...(base || f), links: [...((base || f).links || []), { title: newLink.title.trim(), url: full }] };
    setF(next); setNewLink({ title: "", url: "" });
    return next;
  };
  const rmLink = (i) => setF({ ...f, links: (f.links || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!f.title.trim()) { toast.error("Give your moment a title"); return; }
    if (!f.date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      const body = addLink(); // include a link typed but not yet added
      let mid = editing;
      if (editing) await api.patch(`/memoirs/${editing}`, body);
      else { const r = await api.post("/memoirs", body); mid = r.data.id; }
      if (photoFiles.length) {
        const n = await uploadPhotos(mid, photoFiles);
        toast.success(`${editing ? "Memoir updated" : "Moment saved"} — ${n}/${photoFiles.length} photo${photoFiles.length > 1 ? "s" : ""} uploaded`);
      } else {
        toast.success(editing ? "Memoir updated" : "Moment saved to your memoirs");
      }
      setShowForm(false); setEditing(null); setF(EMPTY); setPhotoFiles([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this memoir?")) return;
    try { await api.delete(`/memoirs/${id}`); setItems((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const addNote = async (id) => {
    if (!note.trim()) return;
    try { await api.post(`/memoirs/${id}/comments`, { text: note }); setNote(""); load(); }
    catch { toast.error("Could not add note"); }
  };
  const delNote = async (id, cid) => { try { await api.delete(`/memoirs/${id}/comments/${cid}`); load(); } catch { /* noop */ } };
  const uploadPhotos = async (id, files) => {
    let ok = 0;
    for (const raw of Array.from(files)) {
      const src = raw.file || raw; // {file, caption} from the form, plain File from the detail row
      const file = await compressImage(src);
      if (file.size > MAX_UPLOAD) { toast.error(`${src.name}: still over 20 MB after shrinking — skipped`); continue; }
      const fd = new FormData(); fd.append("file", file);
      if (raw.caption?.trim()) fd.append("caption", raw.caption.trim());
      try { await api.post(`/memoirs/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (e) { toast.error(`${src.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    return ok;
  };
  const delPhoto = async (id, pid) => { try { await api.delete(`/memoirs/${id}/photos/${pid}`); load(); } catch { /* noop */ } };

  // One-tap sharing (same style as story quick-share): first tap creates the link.
  const shareMemoir = async (m, target) => {
    try {
      const r = await api.post(`/memoirs/${m.id}/share`, { enabled: true });
      const url = `${window.location.origin}/memoirs/shared/${r.data.token}`;
      if (target === "copy") { await navigator.clipboard.writeText(url); toast.success("Link copied — anyone with it can view this memoir"); }
      else if (target === "wa") window.open(`https://wa.me/?text=${encodeURIComponent(`${m.title} — a moment from my travels: ${url}`)}`, "_blank");
      else window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
      if (!m.share_enabled) load();
    } catch { toast.error("Could not share"); }
  };
  const unshareMemoir = async (m) => {
    try { await api.post(`/memoirs/${m.id}/share`, { enabled: false }); toast.success("Sharing turned off"); load(); }
    catch { toast.error("Could not update"); }
  };

  const inp = "border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  // Yearly Memoir Book — printable album PDF of a whole year of moments.
  const years = [...new Set((items || []).map((m) => (m.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const [bookYear, setBookYear] = useState("");
  const activeYear = bookYear || years[0];
  const [bookTitle, setBookTitle] = useState("");
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  useEffect(() => {
    if (!activeYear) return;
    api.get(`/memoirs/book/settings?year=${activeYear}`).then((r) => setBookTitle(r.data.title)).catch(() => {});
  }, [activeYear]);
  const saveTitle = async () => {
    try {
      const r = await api.post("/memoirs/book/settings", { year: activeYear, title: titleDraft });
      setBookTitle(r.data.title); setEditTitle(false); toast.success("Book title saved — it prints on every page");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save title"); }
  };
  const downloadBook = async () => {
    const y = bookYear || years[0];
    if (!y) { toast.error("No memoirs yet — add a moment first"); return; }
    const token = await auth.currentUser?.getIdToken();
    window.open(`${API}/memoirs/book/pdf?year=${y}&auth=${token}`, "_blank");
  };

  // Email the yearly album to a family member as a gift (download link, no account needed).
  const [showGift, setShowGift] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftMsg, setGiftMsg] = useState("");
  const [sendingGift, setSendingGift] = useState(false);
  const sendGift = async () => {
    const y = bookYear || years[0];
    if (!giftEmail.trim()) { toast.error("Enter the recipient's email"); return; }
    setSendingGift(true);
    try {
      const r = await api.post("/memoirs/book/email", { year: y, to_email: giftEmail, message: giftMsg });
      toast.success(`Memoir Book ${y} sent to ${r.data.sent_to} — what a gift!`);
      setShowGift(false); setGiftEmail(""); setGiftMsg("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the email"); }
    finally { setSendingGift(false); }
  };

  return (
    <div data-testid="memoirs-section">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h2 className="font-heading font-900 text-indigo-700 text-2xl">
          <Link to="/travel-diaries" data-testid="memoirs-diaries-link" className="flex items-center gap-2 hover:underline decoration-2 underline-offset-4">
            <NotebookPen className="w-6 h-6" /> My Travel Diaries — My Memoirs
          </Link>
        </h2>
        <button onClick={openNew} data-testid="memoir-add-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 text-white bg-indigo-600 hover:bg-indigo-700 transition-colors">
          <Plus className="w-4 h-4" /> Add an Activity
        </button>
        {years.length > 0 && (
          <span className="inline-flex items-center" data-testid="memoir-book-controls">
            {years.length > 1 && (
              <select value={bookYear || years[0]} onChange={(e) => setBookYear(e.target.value)} data-testid="memoir-book-year"
                className="border border-indigo-600 border-r-0 bg-white text-indigo-700 px-2 py-1.5 text-sm font-700 outline-none">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            <button onClick={downloadBook} data-testid="memoir-book-btn" title="Download a printable album of a whole year of memoirs"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 border border-indigo-600 text-indigo-700 hover:bg-indigo-50 transition-colors">
              <BookOpen className="w-4 h-4" /> Memoir Book (PDF)
            </button>
            <button onClick={() => setShowGift((v) => !v)} data-testid="memoir-book-gift-btn" title="Email the yearly album to a family member as a gift"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 border border-l-0 border-rose-500 text-rose-600 hover:bg-rose-50 transition-colors">
              <Gift className="w-4 h-4" /> Email as gift
            </button>
          </span>
        )}
      </div>
      <p className="text-sm italic text-indigo-900/70 mb-2" data-testid="memoirs-tagline">Every Moment Has a Story. Keep Yours.</p>

      {activeYear && (
        <div className="flex flex-wrap items-center gap-2 mb-5 text-sm" data-testid="memoir-book-title-row">
          <span className="text-muted-foreground">Book title:</span>
          {editTitle ? (
            <>
              <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} maxLength={70}
                onKeyDown={(e) => e.key === "Enter" && saveTitle()} data-testid="memoir-book-title-input"
                className={`${inp} min-w-[240px]`} />
              <button onClick={saveTitle} data-testid="memoir-book-title-save"
                className="px-3 py-1.5 text-xs font-700 text-white bg-indigo-600 hover:bg-indigo-700 transition-colors">Save</button>
              <button onClick={() => setEditTitle(false)} className="text-xs text-muted-foreground hover:underline">Cancel</button>
            </>
          ) : (
            <>
              <span className="font-700 text-indigo-800" data-testid="memoir-book-title-current">{bookTitle || `My Memoir Book — ${activeYear}`}</span>
              <button onClick={() => { setTitleDraft(bookTitle || `My Memoir Book — ${activeYear}`); setEditTitle(true); }} data-testid="memoir-book-title-change"
                className="text-xs font-700 text-indigo-600 underline underline-offset-2 hover:text-indigo-800">Change</button>
            </>
          )}
        </div>
      )}

      {showGift && (
        <div className="border border-rose-200 bg-rose-50/40 p-4 mb-6 space-y-2" data-testid="memoir-gift-panel">
          <p className="text-sm text-zinc-700">
            <b>Send your {bookYear || years[0]} Memoir Book as a gift</b> — they'll get a warm email with your note and a download link. No account needed.
          </p>
          <div className="flex flex-wrap gap-2">
            <input value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} type="email" data-testid="memoir-gift-email"
              placeholder="Family member's email" className={`flex-1 min-w-[220px] ${inp}`} />
          </div>
          <textarea value={giftMsg} onChange={(e) => setGiftMsg(e.target.value)} rows={2} data-testid="memoir-gift-message"
            placeholder="Add a personal note (optional) — e.g. Mom, here's our year in moments…" className={`w-full ${inp} resize-y`} />
          <button onClick={sendGift} disabled={sendingGift} data-testid="memoir-gift-send"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 transition-colors">
            <Send className="w-4 h-4" /> {sendingGift ? "Sending…" : "Send the gift"}
          </button>
        </div>
      )}

      {showForm && (
        <div className="border border-indigo-200 bg-indigo-50/40 p-4 mb-6 space-y-2" data-testid="memoir-form">
          <div className="flex items-center justify-between">
            <div className="font-700 text-sm">{editing ? "Edit memoir" : "New activity / moment"}</div>
            <button onClick={() => { setShowForm(false); setEditing(null); }} data-testid="memoir-form-close" className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
          </div>
          <input value={f.title} onChange={set("title")} data-testid="memoir-title" placeholder="What did you do? (e.g. Sunset hike at Fort Kochi)" className={`w-full ${inp}`} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select value={f.activity_type} onChange={set("activity_type")} data-testid="memoir-type" className={`${inp} bg-white notranslate`} translate="no">
              {ACTIVITY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input type="date" value={f.date} onChange={set("date")} data-testid="memoir-date" className={inp} />
            <input type="time" value={f.time} onChange={set("time")} data-testid="memoir-time" className={inp} />
            <input value={f.location} onChange={set("location")} data-testid="memoir-location" placeholder="Location (optional)" className={inp} />
          </div>
          {f.activity_type === "other" && (
            <input value={f.activity_other} onChange={set("activity_other")} data-testid="memoir-type-other" placeholder="Describe the activity type" className={`w-full ${inp}`} />
          )}
          <textarea value={f.notes} onChange={set("notes")} data-testid="memoir-notes" rows={3} placeholder="Your memory, notes, who was there, what made it special…" className={`w-full ${inp} resize-y`} />

          <div className="flex flex-wrap items-center gap-2" data-testid="memoir-form-photos-row">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 border border-zinc-300 bg-white cursor-pointer hover:border-indigo-500 transition-colors">
              <ImagePlus className="w-4 h-4 text-indigo-600" /> Add photos
              <input type="file" accept="image/*" multiple className="hidden" data-testid="memoir-form-photos"
                onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; setPhotoFiles((p) => [...p, ...files.map((file) => ({ file, caption: "" }))]); }} />
            </label>
            {photoFiles.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-white border border-zinc-300 pl-2 py-1" data-testid={`memoir-form-photo-chip-${i}`}>
                <span className="max-w-[120px] truncate">{p.file.name}</span>
                <input value={p.caption} onChange={(e) => setPhotoFiles((prev) => prev.map((x, idx) => idx === i ? { ...x, caption: e.target.value } : x))}
                  placeholder="Caption…" data-testid={`memoir-form-photo-caption-${i}`}
                  className="w-32 border-l border-zinc-200 px-1.5 py-0.5 text-xs outline-none focus:bg-indigo-50/50" />
                <button onClick={() => setPhotoFiles((prev) => prev.filter((_, idx) => idx !== i))} className="px-1 text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {photoFiles.length > 0 && (
              <span className="text-xs text-muted-foreground">{photoFiles.length} photo{photoFiles.length > 1 ? "s" : ""} will upload on save · large ones auto-shrunk (max 20 MB)</span>
            )}
          </div>

          <div className="space-y-1.5" data-testid="memoir-form-links-row">
            {(f.links || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`memoir-form-link-${i}`}>
                <Link2 className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <a href={l.url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline truncate">{l.title || l.url}</a>
                <button onClick={() => rmLink(i)} data-testid={`memoir-form-link-del-${i}`} className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <input value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} data-testid="memoir-link-title"
                placeholder="Link title (optional)" className={`${inp} w-44`} />
              <input value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && addLink()} data-testid="memoir-link-url"
                placeholder="Paste a link — blog, map, video…" className={`flex-1 min-w-[200px] ${inp}`} />
              <button onClick={() => addLink()} data-testid="memoir-link-add"
                className="px-3 py-1.5 text-sm font-700 border border-zinc-300 bg-white hover:border-indigo-500 transition-colors">+ Add link</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select value={f.trip_id} onChange={set("trip_id")} data-testid="memoir-trip" className={`${inp} bg-white min-w-[220px]`}>
              <option value="">Outside a trip — everyday moment</option>
              {trips.map((t) => <option key={t.id} value={t.id}>{t.title} ({t.start_date || "no date"})</option>)}
            </select>
            {f.trip_id && (
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer" data-testid="memoir-part-of-trip">
                <input type="checkbox" checked={f.part_of_trip} onChange={set("part_of_trip")} />
                Part of the trip itinerary
              </label>
            )}
            <button onClick={save} disabled={saving} data-testid="memoir-save"
              className="ml-auto px-4 py-2 text-sm font-700 text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {saving ? "Saving…" : editing ? "Update memoir" : "Save moment"}
            </button>
          </div>
        </div>
      )}

      {!items ? <p className="text-sm text-muted-foreground">Loading your memoirs…</p>
        : items.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-6 text-center" data-testid="memoirs-empty">
            No memoirs yet — press <b>Add an Activity</b> to keep your first moment: a hike, a picnic, a meal, a memory.
          </p>
        ) : (
          <div className="space-y-2" data-testid="memoirs-list">
            {items.map((m) => (
              <div key={m.id} className="border border-zinc-300 bg-white p-3.5 flex items-start gap-3" data-testid={`memoir-${m.id}`}>
                <div className="text-center shrink-0 w-12">
                  <div className="font-mono text-[10px] text-muted-foreground uppercase">{new Date(`${m.date}T00:00:00`).toLocaleDateString("en-US", { month: "short" })}</div>
                  <div className="font-heading font-900 text-xl text-indigo-700 leading-none">{new Date(`${m.date}T00:00:00`).getDate()}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{m.date.slice(0, 4)}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-700">{m.title}</span>
                    <span className="text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded notranslate">
                      {m.activity_type === "other" && m.activity_other ? `➕ ${m.activity_other}` : TYPE_LABEL[m.activity_type] || m.activity_type}
                    </span>
                    {m.trip_id ? (
                      <span className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 rounded ${m.part_of_trip ? "bg-cyan-50 text-cyan-800 border border-cyan-200" : "bg-zinc-100 text-zinc-600 border border-zinc-200"}`} data-testid={`memoir-trip-badge-${m.id}`}>
                        <Luggage className="w-3 h-3" /> {m.trip_title}{!m.part_of_trip && " (outside itinerary)"}
                      </span>
                    ) : (
                      <span className="text-[11px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded" data-testid={`memoir-outside-badge-${m.id}`}>Everyday moment</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                    {m.time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.time}</span>}
                    {m.location && (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location)}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-indigo-700 hover:underline" title="Open in Google Maps">
                        <MapPin className="w-3 h-3" /> {m.location}
                      </a>
                    )}
                  </div>
                  {m.notes && <p className="text-sm text-zinc-700 mt-1 whitespace-pre-line">{m.notes}</p>}
                  {(m.links || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1.5" data-testid={`memoir-links-${m.id}`}>
                      {m.links.map((l) => (
                        <a key={l.id || l.url} href={l.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-700 text-indigo-700 border border-indigo-200 bg-indigo-50/60 px-2 py-0.5 hover:bg-indigo-100 transition-colors">
                          <ExternalLink className="w-3 h-3" /> {l.title || (() => { try { return new URL(l.url).hostname.replace(/^www\./, ""); } catch { return l.url; } })()}
                        </a>
                      ))}
                    </div>
                  )}
                  {(m.photos || []).length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap" data-testid={`memoir-photos-${m.id}`}>
                      {m.photos.map((ph) => (
                        <div key={ph.id} className="relative group">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}${imgTok ? `?auth=${imgTok}` : ""}`} alt={ph.caption || ""} className="w-20 h-20 object-cover border border-zinc-200" />
                          <button onClick={() => delPhoto(m.id, ph.id)} data-testid={`memoir-photo-del-${ph.id}`} className="absolute -top-1.5 -right-1.5 bg-white border rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {openId === m.id && (
                    <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1.5" data-testid={`memoir-detail-${m.id}`}>
                      {(m.comments || []).map((c) => (
                        <div key={c.id} className="text-sm flex items-start gap-2">
                          <span className="flex-1">{c.text}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{(c.created_at || "").slice(0, 10)}</span>
                          <button onClick={() => delNote(m.id, c.id)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote(m.id)}
                          data-testid={`memoir-note-input-${m.id}`} placeholder="Add a note or comment to this moment…"
                          className="flex-1 border border-zinc-300 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
                        <button onClick={() => addNote(m.id)} data-testid={`memoir-note-add-${m.id}`} className="px-3 py-1.5 text-xs font-700 border border-zinc-300 hover:border-indigo-500">Add</button>
                        <label className="cursor-pointer text-muted-foreground hover:text-indigo-700" title="Add photos">
                          <ImagePlus className="w-4 h-4" />
                          <input type="file" accept="image/*" multiple className="hidden" data-testid={`memoir-photo-input-${m.id}`}
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []); e.target.value = "";
                              if (!files.length) return;
                              const n = await uploadPhotos(m.id, files);
                              if (n) { toast.success(`${n} photo${n > 1 ? "s" : ""} added`); load(); }
                            }} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex gap-2">
                    <button onClick={() => setOpenId(openId === m.id ? null : m.id)} data-testid={`memoir-open-${m.id}`}
                      className={`inline-flex items-center gap-1 text-xs ${openId === m.id ? "text-indigo-700" : "text-muted-foreground hover:text-indigo-700"}`}>
                      <MessageSquare className="w-4 h-4" /> {(m.comments || []).length + (m.photos || []).length || ""}
                    </button>
                    <button onClick={() => openEdit(m)} data-testid={`memoir-edit-${m.id}`} className="text-muted-foreground hover:text-indigo-700"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(m.id)} data-testid={`memoir-delete-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-1" data-testid={`memoir-quick-share-${m.id}`}>
                    <button onClick={() => shareMemoir(m, "wa")} title="Share on WhatsApp" data-testid={`memoir-share-whatsapp-${m.id}`}
                      className="inline-flex items-center justify-center w-7 h-7 border bg-white text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => shareMemoir(m, "fb")} title="Share on Facebook" data-testid={`memoir-share-facebook-${m.id}`}
                      className="inline-flex items-center justify-center w-7 h-7 border bg-white text-blue-600 border-blue-300 hover:bg-blue-50">
                      <Facebook className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => shareMemoir(m, "copy")} title="Copy share link" data-testid={`memoir-share-copy-${m.id}`}
                      className="inline-flex items-center justify-center w-7 h-7 border bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50">
                      <Link2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {m.share_enabled && (
                    <button onClick={() => unshareMemoir(m)} data-testid={`memoir-unshare-${m.id}`}
                      className="text-[10px] text-muted-foreground hover:text-destructive underline underline-offset-2">
                      Shared — turn off
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
